import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import bannerImage from "../assets/img/ban1.jpg";
import "../assets/css/style.css";


// Static data
const countries = ["USA", "Canada", "UK", "India", "Australia"];
const companyNames = ["TechCorp", "Innovate Inc", "Global Solutions", "NextGen", "AlphaTech"];
const employeeSizes = ["1-10", "11-50", "51-200", "201-500", "501+"];
const companyRevenues = ["< $1M", "$1M-$10M", "$10M-$50M", "$50M-$100M", "$100M+"];
const departments = ["Engineering", "Marketing", "Sales", "HR", "Finance"];
const functions = ["Development", "Analytics", "Management", "Support"];
const seniorityLevels = ["Entry", "Mid", "Senior", "Executive"];
const professionTypes = ["Engineer", "Manager", "Analyst", "Consultant"];
const industries = [
  { name: "Technology", sub: ["Software", "Hardware", { name: "AI", sub: ["Machine Learning", "NLP"] }] },
  { name: "Finance", sub: ["Banking", "Insurance"] },
  { name: "Healthcare", sub: ["Pharma", "Medical Devices"] },
];

const users = [
  {
    member_id: "M001",
    first_name: "John",
    middle_name: "",
    last_name: "Doe",
    country: "USA",
    company_name: "TechCorp",
    job_title: "Software Engineer",
    employee_size: "51-200",
    company_revenue: "$10M-$50M",
    department: "Engineering",
    function: "Development",
    seniority_level: "Senior",
    profession_type: "Engineer",
    sector_and_industry: "Technology > Software",
    email_verified_on: "2025-01-15",
    mobile_verified_on: "2025-01-15",
    subscription_status: "Double Opt-in",
    subscribed_on: "2025-01-10",
    survey_participated: "Yes",
    added_by: "Admin User",
    created_on: "2025-01-10",
    updated_on: "2025-05-15",
  },
  {
    member_id: "M002",
    first_name: "Jane",
    middle_name: "A",
    last_name: "Smith",
    country: "Canada",
    company_name: "Innovate Inc",
    job_title: "Marketing Manager",
    employee_size: "11-50",
    company_revenue: "$1M-$10M",
    department: "Marketing",
    function: "Analytics",
    seniority_level: "Mid",
    profession_type: "Manager",
    sector_and_industry: "Finance > Banking",
    email_verified_on: "2025-02-10",
    mobile_verified_on: "2025-02-10",
    subscription_status: "Pending",
    subscribed_on: "2025-02-05",
    survey_participated: "No",
    added_by: "Admin User",
    created_on: "2025-02-05",
    updated_on: "2025-05-10",
  },
];

const ITEMS_PER_PAGE = 15;

// Pagination Component
const Pagination = ({ currentPage, totalPages, handlePageChange }) => {
  const pageGroup = Math.ceil(currentPage / 3);
  const startPage = (pageGroup - 1) * 3 + 1;
  const endPage = Math.min(startPage + 2, totalPages);
  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  return (
    <nav aria-label="Page navigation" className="mt-4">
      <ul className="pagination justify-content-center">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Previous
          </button>
        </li>
        {pageNumbers.map((page) => (
          <li
            key={page}
            className={`page-item ${currentPage === page ? "active" : ""}`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          </li>
        ))}
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

const UserPanel = () => {
  const [filters, setFilters] = useState({
    member_id: "",
    first_name: "",
    job_title: "",
    sector_and_industry: "",
    employee_size: [],
    company_revenue: [],
    department: [],
    country: [],
    function: [],
    seniority_level: [],
    profession_type: [],
    company_name: [],
    email_opt: "",
    mobile_opt: "",
    subscription: "",
  });
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(Math.ceil(users.length / ITEMS_PER_PAGE));
  const [showImportModal, setShowImportModal] = useState(false);
  const [industrySearch, setIndustrySearch] = useState("");
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const industryDropdownRef = useRef(null);

  // Handle clicks outside industry dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(event.target)) {
        setIsIndustryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter users based on search criteria
  useEffect(() => {
    const filtered = users.filter((user) => {
      const matchesMemberId = filters.member_id
        ? user.member_id.toLowerCase().includes(filters.member_id.toLowerCase())
        : true;
      const matchesName = filters.first_name
        ? `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(filters.first_name.toLowerCase())
        : true;
      const matchesJobTitle = filters.job_title
        ? user.job_title.toLowerCase().includes(filters.job_title.toLowerCase())
        : true;
      const matchesIndustry = filters.sector_and_industry
        ? user.sector_and_industry === filters.sector_and_industry
        : true;
      const matchesEmployeeSize =
        filters.employee_size.length > 0
          ? filters.employee_size.includes(user.employee_size)
          : true;
      const matchesRevenue =
        filters.company_revenue.length > 0
          ? filters.company_revenue.includes(user.company_revenue)
          : true;
      const matchesDepartment =
        filters.department.length > 0
          ? filters.department.includes(user.department)
          : true;
      const matchesCountry =
        filters.country.length > 0
          ? filters.country.includes(user.country)
          : true;
      const matchesFunction =
        filters.function.length > 0
          ? filters.function.includes(user.function)
          : true;
      const matchesSeniority =
        filters.seniority_level.length > 0
          ? filters.seniority_level.includes(user.seniority_level)
          : true;
      const matchesProfession =
        filters.profession_type.length > 0
          ? filters.profession_type.includes(user.profession_type)
          : true;
      const matchesCompany =
        filters.company_name.length > 0
          ? filters.company_name.includes(user.company_name)
          : true;
      const matchesEmailOpt = filters.email_opt
        ? (filters.email_opt === "1" && user.email_verified_on) ||
          (filters.email_opt === "0" && !user.email_verified_on)
        : true;
      const matchesMobileOpt = filters.mobile_opt
        ? (filters.mobile_opt === "1" && user.mobile_verified_on) ||
          (filters.mobile_opt === "0" && !user.mobile_verified_on)
        : true;
      const matchesSubscription = filters.subscription
        ? user.subscription_status === filters.subscription
        : true;

      return (
        matchesMemberId &&
        matchesName &&
        matchesJobTitle &&
        matchesIndustry &&
        matchesEmployeeSize &&
        matchesRevenue &&
        matchesDepartment &&
        matchesCountry &&
        matchesFunction &&
        matchesSeniority &&
        matchesProfession &&
        matchesCompany &&
        matchesEmailOpt &&
        matchesMobileOpt &&
        matchesSubscription
      );
    });

    setFilteredUsers(filtered);
    setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
    setCurrentPage(1);
  }, [filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (e) => {
    const { name, options } = e.target;
    const values = Array.from(options)
      .filter((opt) => opt.selected)
      .map((opt) => opt.value);
    setFilters((prev) => ({ ...prev, [name]: values }));
  };

  const handleIndustrySelect = (value) => {
    setFilters((prev) => ({ ...prev, sector_and_industry: value }));
    setIsIndustryDropdownOpen(false);
    setIndustrySearch("");
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const clearFilters = () => {
    setFilters({
      member_id: "",
      first_name: "",
      job_title: "",
      sector_and_industry: "",
      employee_size: [],
      company_revenue: [],
      department: [],
      country: [],
      function: [],
      seniority_level: [],
      profession_type: [],
      company_name: [],
      email_opt: "",
      mobile_opt: "",
      subscription: "",
    });
    setIndustrySearch("");
  };

  const filteredIndustries = industries.filter((industry) => {
    const searchLower = industrySearch.toLowerCase();
    return (
      industry.name.toLowerCase().includes(searchLower) ||
      industry.sub.some((sub) =>
        typeof sub === "string"
          ? sub.toLowerCase().includes(searchLower)
          : sub.name.toLowerCase().includes(searchLower) ||
            sub.sub.some((subSub) => subSub.toLowerCase().includes(searchLower))
      )
    );
  });

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div id="wrapper">
      {/* Banner Section */}
      <section
        id="banner"
        style={{
          minHeight: "480px",
          background: `url(${bannerImage}) center left no-repeat`,
          backgroundSize: "cover",
        }}
        className="d-flex py-5"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-7 mx-auto pt-5">
              <div className="text-center pt-5">
                <h1 className="text-white display1 mb-3 fw-700">User Panel</h1>
                <h4 className="fw-400 text-white cbcbcb lh-26">
                  Manage and filter user data efficiently
                </h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section id="inner_section">
        <div className="container">
          <div className="row">
            {/* Sidebar with Filters */}
            <div className="col-lg-4 col-xl-4">
              <aside id="asidebar_inner" className="ft-15 lh-22">
                <div className="first_box card_box">
                  <form
                    className="position-relative search_aside"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <i className="bi bi-search"></i>
                    <input
                      type="text"
                      name="first_name"
                      className="form-control"
                      placeholder="Search by Name"
                      value={filters.first_name}
                      onChange={handleInputChange}
                    />
                    {filters.first_name && (
                      <button
                        className="btn btn-secondary"
                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                        onClick={() =>
                          handleInputChange({
                            target: { name: "first_name", value: "" },
                          })
                        }
                      >
                        Clear
                      </button>
                    )}
                  </form>
                  <div className="d-flex justify-content-between mt-3">
                    <h4 className="fw-700">Filters</h4>
                    <a
                      href="#"
                      className="color746E6E"
                      onClick={(e) => {
                        e.preventDefault();
                        clearFilters();
                      }}
                    >
                      Clear all
                    </a>
                  </div>
                </div>
                <div className="card_box color-555">
                  <div className="search_criteria">
                    <div className="accordion" id="filterAccordion">
                      {[
                        {
                          id: "member_id",
                          icon: "bi-id-card",
                          title: "Member ID",
                          name: "member_id",
                          placeholder: "Enter Member ID",
                        },
                        {
                          id: "job_title",
                          icon: "bi-briefcase",
                          title: "Job Title",
                          name: "job_title",
                          placeholder: "Enter Job Title",
                        },
                      ].map((filter, idx) => (
                        <div className="accordion-item" key={idx}>
                          <h2
                            className="accordion-header"
                            id={`heading${filter.id}`}
                          >
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapse${filter.id}`}
                              aria-expanded="false"
                              aria-controls={`collapse${filter.id}`}
                            >
                              <i
                                className={`bi ${filter.icon}`}
                                style={{ marginRight: "8px", fontSize: "1rem" }}
                                aria-hidden="true"
                              ></i>
                              {filter.title}
                            </button>
                          </h2>
                          <div
                            id={`collapse${filter.id}`}
                            className="accordion-collapse collapse"
                            aria-labelledby={`heading${filter.id}`}
                            data-bs-parent="#filterAccordion"
                          >
                            <div className="accordion-body">
                              <div style={{ display: "flex", gap: "10px" }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  name={filter.name}
                                  placeholder={filter.placeholder}
                                  value={filters[filter.name]}
                                  onChange={handleInputChange}
                                />
                                {filters[filter.name] && (
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() =>
                                      handleInputChange({
                                        target: { name: filter.name, value: "" },
                                      })
                                    }
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {[
                        {
                          id: "country",
                          icon: "bi-geo-alt",
                          title: "Country",
                          name: "country",
                          options: countries,
                        },
                        {
                          id: "company_name",
                          icon: "bi-building",
                          title: "Company Name",
                          name: "company_name",
                          options: companyNames,
                        },
                        {
                          id: "employee_size",
                          icon: "bi-people",
                          title: "Employee Size",
                          name: "employee_size",
                          options: employeeSizes,
                        },
                        {
                          id: "company_revenue",
                          icon: "bi-currency-dollar",
                          title: "Company Revenue",
                          name: "company_revenue",
                          options: companyRevenues,
                        },
                        {
                          id: "department",
                          icon: "bi-sitemap",
                          title: "Department",
                          name: "department",
                          options: departments,
                        },
                        {
                          id: "function",
                          icon: "bi-gear",
                          title: "Function",
                          name: "function",
                          options: functions,
                        },
                        {
                          id: "seniority_level",
                          icon: "bi-star",
                          title: "Seniority Level",
                          name: "seniority_level",
                          options: seniorityLevels,
                        },
                        {
                          id: "profession_type",
                          icon: "bi-person-badge",
                          title: "Profession Type",
                          name: "profession_type",
                          options: professionTypes,
                        },
                      ].map((filter, idx) => (
                        <div className="accordion-item" key={idx}>
                          <h2
                            className="accordion-header"
                            id={`heading${filter.id}`}
                          >
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapse${filter.id}`}
                              aria-expanded="false"
                              aria-controls={`collapse${filter.id}`}
                            >
                              <i
                                className={`bi ${filter.icon}`}
                                style={{ marginRight: "8px", fontSize: "1rem" }}
                                aria-hidden="true"
                              ></i>
                              {filter.title}
                            </button>
                          </h2>
                          <div
                            id={`collapse${filter.id}`}
                            className="accordion-collapse collapse"
                            aria-labelledby={`heading${filter.id}`}
                            data-bs-parent="#filterAccordion"
                          >
                            <div className="accordion-body">
                              <div style={{ display: "flex", gap: "10px" }}>
                                <select
                                  className="form-control"
                                  name={filter.name}
                                  multiple
                                  value={filters[filter.name]}
                                  onChange={handleMultiSelectChange}
                                >
                                  {filter.options.map((option, index) => (
                                    <option key={index} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                                {filters[filter.name].length > 0 && (
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() =>
                                      handleMultiSelectChange({
                                        target: {
                                          name: filter.name,
                                          options: [],
                                        },
                                      })
                                    }
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="accordion-item">
                        <h2 className="accordion-header" id="headingIndustry">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseIndustry"
                            aria-expanded="false"
                            aria-controls="collapseIndustry"
                          >
                            <i
                              className="bi bi-briefcase"
                              style={{ marginRight: "8px", fontSize: "1rem" }}
                              aria-hidden="true"
                            ></i>
                            Industry
                          </button>
                        </h2>
                        <div
                          id="collapseIndustry"
                          className="accordion-collapse collapse"
                          aria-labelledby="headingIndustry"
                          data-bs-parent="#filterAccordion"
                        >
                          <div className="accordion-body">
                            <div style={{ display: "flex", gap: "10px" }} ref={industryDropdownRef}>
                              <div className="dropdown" style={{ position: "relative", width: "100%" }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search Industry"
                                  value={industrySearch}
                                  onChange={(e) => setIndustrySearch(e.target.value)}
                                  onFocus={() => setIsIndustryDropdownOpen(true)}
                                />
                                {isIndustryDropdownOpen && (
                                  <ul
                                    className="dropdown-menu show"
                                    style={{
                                      maxHeight: "300px",
                                      overflowY: "auto",
                                      width: "100%",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {filteredIndustries.length > 0 ? (
                                      filteredIndustries.map((industry, index) => (
                                        <li key={index}>
                                          <div
                                            className="dropdown-item"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleIndustrySelect(industry.name)}
                                          >
                                            {industry.name}
                                          </div>
                                          {industry.sub.map((sub, subIndex) =>
                                            typeof sub === "string" ? (
                                              <ul key={subIndex} style={{ listStyle: "none", paddingLeft: "15px" }}>
                                                <li
                                                  className="dropdown-item"
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() => handleIndustrySelect(`${industry.name} > ${sub}`)}
                                                >
                                                  {sub}
                                                </li>
                                              </ul>
                                            ) : (
                                              <ul key={subIndex} style={{ listStyle: "none", paddingLeft: "15px" }}>
                                                <li
                                                  className="dropdown-item"
                                                  style={{ cursor: "pointer" }}
                                                  onClick={() => handleIndustrySelect(`${industry.name} > ${sub.name}`)}
                                                >
                                                  {sub.name}
                                                </li>
                                                {sub.sub.map((subSub, subSubIndex) => (
                                                  <ul
                                                    key={subSubIndex}
                                                    style={{ listStyle: "none", paddingLeft: "15px" }}
                                                  >
                                                    <li
                                                      className="dropdown-item"
                                                      style={{ cursor: "pointer" }}
                                                      onClick={() =>
                                                        handleIndustrySelect(
                                                          `${industry.name} > ${sub.name} > ${subSub}`
                                                        )
                                                      }
                                                    >
                                                      {subSub}
                                                    </li>
                                                  </ul>
                                                ))}
                                              </ul>
                                            )
                                          )}
                                        </li>
                                      ))
                                    ) : (
                                      <li className="dropdown-item">No industries found</li>
                                    )}
                                  </ul>
                                )}
                              </div>
                              {filters.sector_and_industry && (
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleIndustrySelect("")}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {[
                        ,
                      ].map((filter, idx) => (
                        <div className="accordion-item" key={idx}>
                          <h2
                            className="accordion-header"
                            id={`heading${filter.id}`}
                          >
                            <button
                              className="accordion-button collapsed"
                              type="button"
                              data-bs-toggle="collapse"
                              data-bs-target={`#collapse${filter.id}`}
                              aria-expanded="false"
                              aria-controls={`collapse${filter.id}`}
                            >
                              <i
                                className={`bi ${filter.icon}`}
                                style={{ marginRight: "8px", fontSize: "1rem" }}
                                aria-hidden="true"
                              ></i>
                              {filter.title}
                            </button>
                          </h2>
                          <div
                            id={`collapse${filter.id}`}
                            className="accordion-collapse collapse"
                            aria-labelledby={`heading${filter.id}`}
                            data-bs-parent="#filterAccordion"
                          >
                            <div className="accordion-body">
                              <div style={{ display: "flex", gap: "10px" }}>
                                <select
                                  className="form-control"
                                  name={filter.name}
                                  value={filters[filter.name]}
                                  onChange={handleInputChange}
                                >
                                  <option value="">Select {filter.title}</option>
                                  {filter.options.map((option, index) => (
                                    <option
                                      key={index}
                                      value={typeof option === "string" ? option : option.value}
                                    >
                                      {typeof option === "string" ? option : option.label}
                                    </option>
                                  ))}
                                </select>
                                {filters[filter.name] && (
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() =>
                                      handleInputChange({
                                        target: { name: filter.name, value: "" },
                                      })
                                    }
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="inner_wrapper card_box py-5">
                {/* Import Modal */}
                {showImportModal && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1000,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "8px",
                        width: "500px",
                        maxWidth: "90%",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      }}
                    >
                      <div
                        style={{
                          padding: "15px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "1px solid #d1d3e2",
                        }}
                      >
                        <h5 style={{ margin: 0, color: "#2f8c8e" }}>
                          Import Multiple Users
                        </h5>
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            color: "red",
                            fontSize: "1.5rem",
                            cursor: "pointer",
                          }}
                          onClick={() => setShowImportModal(false)}
                        >
                          ×
                        </button>
                      </div>
                      <div style={{ padding: "20px" }}>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            toast.info("File uploaded (placeholder)");
                            setShowImportModal(false);
                          }}
                        >
                          <div style={{ marginBottom: "15px" }}>
                            <label
                              htmlFor="importcsv"
                              style={{
                                fontSize: "1.1rem",
                                color: "#333",
                                fontWeight: "bold",
                                display: "block",
                                marginBottom: "5px",
                              }}
                            >
                              Browse CSV File <small>(Only .csv files allowed)</small>
                            </label>
                            <input
                              type="file"
                              id="importcsv"
                              name="importcsv"
                              accept=".csv"
                              required
                              style={{
                                width: "100%",
                                padding: "10px",
                                borderRadius: "8px",
                                border: "none",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                              }}
                            />
                          </div>
                          <button
                            type="submit"
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              background: "#2f8c8e",
                              color: "#fff",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            Upload <i className="bi bi-upload"></i>
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header with Actions */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    padding: "0 15px",
                  }}
                >
                  <h1 style={{ fontSize: "1.75rem", fontWeight: "bold", color: "#333" }}>
                    User Panel
                  </h1>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <Link
                      to="/Addinpuser"
                      style={{
                        padding: "10px 20px",
                        borderRadius: "25px",
                        background: "#2f8c8e",
                        color: "#fff",
                        textDecoration: "none",
                        fontSize: "0.875rem",
                      }}
                    >
                      <i className="bi bi-plus"></i> Add User
                    </Link>
                    <button
                      style={{
                        padding: "10px 20px",
                        borderRadius: "25px",
                        background: "#2f8c8e",
                        color: "#fff",
                        border: "none",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowImportModal(true)}
                    >
                      <i className="bi bi-upload"></i> Import Bulk Data
                    </button>
                    <a
                      href="/sample.csv"
                      style={{
                        padding: "10px 20px",
                        borderRadius: "25px",
                        background: "#6c757d",
                        color: "#fff",
                        textDecoration: "none",
                        fontSize: "0.875rem",
                      }}
                    >
                      <i className="bi bi-download"></i> Download Sample CSV
                    </a>
                  </div>
                </div>

                {/* User Table */}
                <div className="tab-content pt-3">
                  <div className="tab-pane fade show active">
                    <section className="table_section table-responsive">
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th scope="col">Member ID</th>
                            <th scope="col">Full Name</th>
                            <th scope="col">Country</th>
                            <th scope="col">Company</th>
                            <th scope="col">Title</th>
                            <th scope="col">Company Size</th>
                            <th scope="col">Company Revenue</th>
                            <th scope="col">Department</th>
                            <th scope="col">Function</th>
                            <th scope="col">Seniority Level</th>
                            <th scope="col">Profession Type</th>
                            <th scope="col">Industry</th>
                            <th scope="col">Added By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.length > 0 ? (
                            paginatedUsers.map((user, index) => (
                              <tr key={index}>
                                <td>
                                  <Link
                                    to={`/edit-panel/${user.member_id}`}
                                    title={user.member_id || "NA"}
                                  >
                                    <i className="bi bi-id-card" aria-hidden="true"></i>
                                    {user.member_id || "NA"}
                                  </Link>
                                </td>
                                <td title={`${user.first_name} ${user.middle_name} ${user.last_name}`.trim() || "NA"}>
                                  {`${user.first_name} ${user.middle_name} ${user.last_name}`.trim() || "NA"}
                                </td>
                                <td title={user.country || "NA"}>{user.country || "NA"}</td>
                                <td title={user.company_name || "NA"}>{user.company_name || "NA"}</td>
                                <td title={user.job_title || "NA"}>{user.job_title || "NA"}</td>
                                <td title={user.employee_size || "NA"}>{user.employee_size || "NA"}</td>
                                <td title={user.company_revenue || "NA"}>{user.company_revenue || "NA"}</td>
                                <td title={user.department || "NA"}>
                                  <i className="bi bi-sitemap" aria-hidden="true"></i>
                                  {user.department || "NA"}
                                </td>
                                <td title={user.function || "NA"}>{user.function || "NA"}</td>
                                <td title={user.seniority_level || "NA"}>{user.seniority_level || "NA"}</td>
                                <td title={user.profession_type || "NA"}>{user.profession_type || "NA"}</td>
                                <td title={user.sector_and_industry || "NA"}>{user.sector_and_industry || "NA"}</td>
                                <td title={user.added_by || "NA"}>{user.added_by || "NA"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="13" className="text-center">
                                No users found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </section>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserPanel;
